'use client'
import { cloneElement, Fragment, type ReactElement, type ReactNode, useContext } from 'react'

import { Assign, CreditCardBase, Folder, Hash, Lock, Workspace } from '@doist/icons/brand'
import { Box, Button, Container, Divider, Hidden, Image, Text } from '@doist/marketist'

import { useTranslation } from 'app/i18n/client'
import { GetStartedButtonSimple } from 'components/Get-Started-Button/Get-Started-Button'
import { useLocale } from 'next-intl'
import { useCloudinaryImageLocal } from 'services/hooks/use-cloudinary-local'
import { useCTAsData } from 'services/hooks/use-ctas-data'

import { PricingInfo } from './pricing-info/pricing-info'
import { TooltipFeature } from './tooltip'

import css from './pricing-box.module.css'

import { UserContextV2 } from '@/app/services/providers/contexts/user-context'

function PricingBox() {
    const locale = useLocale()
    const { t } = useTranslation(locale, 'teams')
    const { user } = useContext(UserContextV2)
    const ctaData = useCTAsData('custom-page-pricing-box')

    const backgroundImage = useCloudinaryImageLocal(
        `${process.env.NEXT_PUBLIC_BASE_URL}/static/teams/pricing-box-background.svg`,
    )

    const buttons = []
    if (!user) {
        buttons.push(<GetStartedButtonSimple size="large" className={css.button} />)
    }

    if (ctaData) {
        buttons.push(
            <Button
                variant="primary"
                size="large"
                href={ctaData.href}
                className={ctaData.className}
                {...ctaData.dataAttributes}
            >
                {ctaData.text}
            </Button>,
        )
    }
    buttons.push(
        <Button variant="secondary" size="large" href="/pricing" className={css.button}>
            {t('seeFullPricingDetails')}
        </Button>,
    )

    return (
        <Box className={css.wrapper} style={{ '--background-image': `url(${backgroundImage})` }}>
            <Container width="sm" className={css.container}>
                <Box className={css.pricingBox}>
                    <Box className={css.top}>
                        <Box className={css.left}>
                            <Image
                                src={`${process.env.NEXT_PUBLIC_BASE_URL}/static/teams/pricing-icon.svg`}
                                width={161}
                                height={100}
                                className={css.illustration}
                            />
                            <Text size="major-base" className={css.title}>
                                {t('simpleTransparentPricing')}
                            </Text>
                            <TooltipFeature
                                name={t('createATeamForFree')}
                                description={t('eachTeam')}
                            />
                            <PricingInfo plan="teams" />
                        </Box>
                        <Hidden above="md">
                            <Divider className={css.divider} />
                        </Hidden>
                        <Box className={css.right}>
                            <Box tag="ul" className={css.featureItems}>
                                <FeatureItem icon={<Workspace />}>
                                    {t('aSharedTeamWorkspace')}
                                </FeatureItem>
                                <FeatureItem icon={<Hash />}>
                                    {t('upToTeamProjects', { count: 500 })}
                                </FeatureItem>
                                <FeatureItem icon={<Assign />}>
                                    {t('teamMembersAndGuests')}
                                </FeatureItem>
                                <FeatureItem icon={<Folder />}>
                                    {t('projectFoldersCount')}
                                </FeatureItem>
                                <FeatureItem icon={<Lock />}>
                                    {t('teamRolesAndPermissions')}
                                </FeatureItem>
                                <FeatureItem icon={<CreditCardBase />}>
                                    {t('teamBilling')}
                                </FeatureItem>
                            </Box>
                        </Box>
                    </Box>
                    <Hidden below="md">
                        <Divider className={css.divider} />
                    </Hidden>
                    <Box className={css.buttonRow}>
                        {buttons.map((button, index) => (
                            <Fragment key={index}>{button}</Fragment>
                        ))}
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

function FeatureItem({ icon, children }: { icon: ReactElement; children: ReactNode }) {
    const Icon = cloneElement(icon, {
        width: 20,
        height: 20,
        color: 'var(--display-onlight-tertiary)',
    })
    return (
        <Box tag="li" className={css.featureItem}>
            {Icon}
            <Text size="body-sm" weight="medium">
                {children}
            </Text>
        </Box>
    )
}

export { PricingBox }
